import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useReactlytics } from '@americanexpress/reactlytics';
import {
  ERROR, IDLE, LOADING, SUCCESS,
} from '../utils/constants';
import { streamTravelWishlist } from '../utils/streamableApi';
import { LoadingStep } from '../utils/streamableHelpers';

export const useChatMessages = ({
  apiUrl, conversationId,
}) => {
  const intl = useIntl();
  const { track } = useReactlytics();
  const idCounter = useRef(0);

  const nextId = useCallback(() => {
    idCounter.current += 1;
    return idCounter.current;
  }, []);

  const [messageArray, setMessageArray] = useState([]);
  const [loadingStep, setLoadingStep] = useState(LoadingStep.FINDING);
  const [conversationApiStatus, setConversationApiStatus] = useState(IDLE);
  const [inputValue, setInputValue] = useState('');

  const appendErrorMessage = useCallback((message) => {
    setMessageArray((prev) => {
      const last = prev[prev.length - 1];

      if (
        last?.type === 'message'
        && last?.message === message
        && !last?.isSentByUser
      ) {
        return prev.map((item, index) => (index === prev.length - 1
          ? { ...item, pulseAt: Date.now() }
          : item)
        );
      }

      return [
        ...prev,
        {
          id: nextId(),
          type: 'message',
          isSentByUser: false,
          message,
        },
      ];
    });
  }, [nextId, setMessageArray]);

  const sendMessage = useCallback(
    async (message) => {
      if (!conversationId) return;

      const newMessage = {
        id: nextId(),
        type: 'message',
        isSentByUser: true,
        message,
      };

      setMessageArray((prev) => [...prev, newMessage]);
      setConversationApiStatus(LOADING);

      try {
        const result = await streamTravelWishlist({
          apiUrl,
          conversationId,
          message,
          intl,
          track,
          onStepChange: (step) => setLoadingStep(step),
        });

        setConversationApiStatus(SUCCESS);

        setMessageArray((prev) => {
          const nextMessages = [
            ...prev,
            {
              id: nextId(),
              type: 'message',
              isSentByUser: false,
              message: result.agentMessage,
            },
          ];

          if (result.hotels.length > 0) {
            nextMessages.push({
              id: nextId(),
              type: 'hotels',
              hotels: result.hotels,
            });
          }

          return nextMessages;
        });
      } catch {
        setConversationApiStatus(ERROR);
      }
    },
    [apiUrl, conversationId, intl, nextId, track]
  );

  return {
    messageArray,
    loadingStep,
    conversationApiStatus,
    inputValue,
    setInputValue,
    sendMessage,
    appendErrorMessage,
  };
};
